import { useMemo, useState } from "react";
import { useDateRange } from "@shared/hooks/useDateRange";
import { DateRangeFilter } from "@shared/ui/DateRangeFilter";
import { useAudit } from "./queries";
import { HttpError } from "@shared/http/client";
import { exportToExcel } from "@shared/utils/excel-export";
import { useAuth } from "@features/auth/AuthContext";
import { today } from "@shared/utils/date";
import type { AuditEntry } from "@/types/audit";

const ACCIONES_CONOCIDAS = [
  "evaluation.create", "evaluation.update", "evaluation.delete",
  "scheduling.create", "scheduling.batch", "scheduling.batch_delete", "scheduling.delete",
];

const badgeForAction = (accion: string) => {
  if (accion.endsWith(".delete") || accion.endsWith(".batch_delete")) return "badge-danger";
  if (accion.endsWith(".create") || accion.endsWith(".batch")) return "badge-success";
  if (accion.endsWith(".update")) return "badge-warn";
  return "badge-gray";
};

export function AuditoriaPage() {
  const { range } = useDateRange();
  const { user } = useAuth();
  const [actorFilter, setActorFilter] = useState("");
  const [accionFilter, setAccionFilter] = useState("");
  const [targetTypeFilter, setTargetTypeFilter] = useState<"" | "evaluacion" | "programacion">("");

  const { data, isLoading, error } = useAudit({
    from: range.from || undefined,
    to: range.to || undefined,
    actor: actorFilter || undefined,
    accion: accionFilter || undefined,
    targetType: targetTypeFilter || undefined,
    limit: 500,
  });

  const rows = data ?? [];
  const uniqueActors = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => r.actor_usuario && s.add(r.actor_usuario));
    return Array.from(s).sort();
  }, [rows]);

  const onExport = () => {
    void exportToExcel<AuditEntry>(
      rows,
      [
        { key: "ts",            label: "Fecha/Hora" },
        { key: "actor_usuario", label: "Usuario" },
        { key: "accion",        label: "Acción" },
        { key: "target_type",   label: "Tipo target" },
        { key: "target_id",     label: "Target id" },
        { key: "ip",            label: "IP" },
        { key: "detalle",       label: "Detalle", get: (r) => r.detalle ? JSON.stringify(r.detalle) : "" },
      ],
      `auditoria-${today()}`,
      {
        reportTitle: "FOCA — Auditoría de acciones",
        rangeFrom: range.from,
        rangeTo: range.to,
        generatedBy: user?.name || user?.username || "",
      }
    );
  };

  return (
    <div className="p-24">
      <div className="dashboard-header">
        <h2 className="text-lg">
          Auditoría
          {rows.length > 0 && <span className="badge badge-blue ml-8">{rows.length}</span>}
        </h2>
        <DateRangeFilter />
      </div>

      <div className="filter-row mt-16">
        <select value={actorFilter} onChange={(e) => setActorFilter(e.target.value)} className="filter-select">
          <option value="">Todos los usuarios</option>
          {uniqueActors.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={accionFilter} onChange={(e) => setAccionFilter(e.target.value)} className="filter-select">
          <option value="">Todas las acciones</option>
          {ACCIONES_CONOCIDAS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={targetTypeFilter} onChange={(e) => setTargetTypeFilter(e.target.value as typeof targetTypeFilter)} className="filter-select">
          <option value="">Todos los targets</option>
          <option value="evaluacion">evaluación</option>
          <option value="programacion">programación</option>
        </select>
        <button type="button" className="btn btn-sm btn-secondary" onClick={onExport} disabled={rows.length === 0}>
          📥 Exportar Excel ({rows.length})
        </button>
      </div>

      {error instanceof HttpError && (
        <div className="alert-inline alert-inline--danger mt-16">Error cargando auditoría: {error.message}</div>
      )}
      {isLoading && <div className="text-muted mt-16">Cargando…</div>}
      {!isLoading && rows.length === 0 && (
        <div className="text-muted mt-24">Sin registros de auditoría con los filtros actuales.</div>
      )}

      {rows.length > 0 && (
        <div className="table-scroll mt-16">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ minWidth: 160 }}>Fecha/Hora</th>
                <th>Usuario</th>
                <th>Acción</th>
                <th>Target</th>
                <th>Detalle</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ whiteSpace: "nowrap", fontFamily: "monospace", fontSize: 12 }}>
                    {new Date(r.ts).toLocaleString("es-CO")}
                  </td>
                  <td>{r.actor_usuario || "—"}</td>
                  <td>
                    <span className={`badge ${badgeForAction(r.accion)}`}>{r.accion}</span>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {r.target_type}{r.target_id != null ? `:${r.target_id}` : ""}
                  </td>
                  <td style={{ fontSize: 11, fontFamily: "monospace", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {r.detalle ? JSON.stringify(r.detalle).slice(0, 200) : "—"}
                  </td>
                  <td style={{ fontSize: 11, fontFamily: "monospace" }}>{r.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
